import { formatSkillName } from '../tools/LoadSkillTool';
import { Tool } from '../tools/Tool';

import { findHtmlDesignSkill, getHtmlDesignSkills } from './skills/skillRegistry';

import type { ToolDefinition, ToolExecutionContext, ToolResult } from 'js/src/agent/types';

interface LoadSkillArgs {
    name: string;
}

/**
 * The html-design agent's load_skill — same progressive-disclosure contract as codeo's tool of the
 * same name, but reading the html-design skill registry (techniques rewritten to the HtmlAsset
 * composition contract) instead of codeo's.
 */
export class LoadHtmlDesignSkillTool extends Tool<LoadSkillArgs> {
    public static readonly toolName = 'load_skill';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: LoadHtmlDesignSkillTool.toolName,
                description:
                    'Load the full implementation for one of the animation skills listed under ' +
                    '"Animation skills". Call this for each relevant skill BEFORE authoring the ' +
                    'composition or edit that uses the technique.',
                parameters: {
                    type: 'object',
                    properties: {
                        name: {
                            type: 'string',
                            description: 'The exact name of the skill to load.',
                        },
                    },
                    required: ['name'],
                },
            },
        };
    }

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<LoadSkillArgs>;
                if (parsed.name) {
                    return `Using skill: ${formatSkillName(parsed.name)}`;
                }
            } catch {
                // fall through to default name
            }
        }
        return 'Using skill';
    }

    public get statusIcon(): string {
        return 'school';
    }

    public async execute(args: LoadSkillArgs, _context: ToolExecutionContext): Promise<ToolResult> {
        const skill = findHtmlDesignSkill(args.name);
        if (!skill) {
            const available = getHtmlDesignSkills()
                .map((s) => s.name)
                .join(', ');
            return `Error: No skill named "${args.name}". Available skills: ${available}.`;
        }
        return skill.instructions;
    }
}
